import React, { useState, useEffect } from 'react';

import api from 'services/api';
import { useDebounce } from 'hooks/useDebounce';

import Icon from 'components/Icon';
import Spinner from 'components/Spinner';
import GridFlex from 'components/GridFlex';
import Button from 'components/Button';
import Text from 'components/Text';
import { Input } from 'components/Fields';

import ModalCreateFactory from 'components/ModalCreateFactory';

import Header from '../Header';
import ModalDeleteOrIsActive, { Types } from '../ModalDeleteOrIsActive';

import { Container } from './styles';
import { List, Item, ContainerActions, SwitchTheme } from '../styles';

interface IList {
  id: number;
  description: string;
  isActive?: boolean;
}

const endpoint = '/factories';

const Factory: React.FC = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [list, setList] = useState<IList[]>([]);
  const [loading, setLoading] = useState(false);
  const [openModalIsActive, setOpenModalIsActive] = useState<Types | boolean>(
    false,
  );
  const [factorySelected, setFactorySelected] = useState<any>(false);

  const getFactories = async () => {
    setLoading(true);

    const newList: IList[] = await api.get(`${endpoint}?description=${search}`);

    setList(newList);

    setLoading(false);
  };

  const autoSave = useDebounce(search);

  const saveChange = async () => {
    getFactories();
  };

  useEffect(() => {
    saveChange();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoSave]);

  return (
    <Container>
      <ModalCreateFactory
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onSaved={() => getFactories()}
      />

      <Header
        onCreate={() => setModalOpen(true)}
        title="Factory management"
        titleCreate="Create new factory"
      />

      {!!openModalIsActive && (
        <ModalDeleteOrIsActive
          type={openModalIsActive as Types}
          endpoint={endpoint}
          itemName={factorySelected.description}
          id={factorySelected.id}
          isActive={factorySelected.isActive}
          onClose={() => setOpenModalIsActive(false)}
          onCreated={() => {
            if (openModalIsActive === Types.DELETE) {
              setList(list.filter(i => i.id !== factorySelected.id));
            }
            if (openModalIsActive === Types.ISACTIVE) {
              const newList = list;

              newList[factorySelected.index] = {
                ...newList[factorySelected.index],
                isActive: !factorySelected.isActive,
              };

              setList(newList);
            }
            setOpenModalIsActive(false);
          }}
        />
      )}

      <Input
        name="search"
        onChange={e => setSearch(e.target.value)}
        value={search}
        placeholder="Search factory"
        width="100%"
        dataTest="search"
        leftContent={
          <Icon
            icon="search"
            color="grayLightText"
            width="18px"
            height="18px"
          />
        }
      />
      <List>
        {loading ? (
          <GridFlex justifyContent="center" alignItems="center">
            <Spinner />
          </GridFlex>
        ) : (
          <>
            {list.map(({ description, id, isActive }, index) => (
              <Item key={`${description}-${id}`}>
                <Text fontSize={14} fontWeight="normal" color="black">
                  {description}
                </Text>
                <ContainerActions>
                  <SwitchTheme
                    checked={!isActive}
                    data-testid={`button-row-isActive-${id}`}
                    onClick={() => {
                      setOpenModalIsActive(Types.ISACTIVE);
                      setFactorySelected({ description, id, isActive, index });
                    }}
                  />
                  <Button
                    width="95px"
                    padding="6px 30px"
                    data-testid={`button-row-delete-${id}`}
                    onClick={() => {
                      setOpenModalIsActive(Types.DELETE);
                      setFactorySelected({ description, id, isActive, index });
                    }}
                  >
                    Delete
                  </Button>
                </ContainerActions>
              </Item>
            ))}
          </>
        )}
      </List>
    </Container>
  );
};

export default Factory;
